import React, { useEffect, useReducer, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  SplashScreen,
  StartScreenWrapper,
  LogoImage,
  MainContentContainer,
  MainContentHeader,
  SignInButtonWrapper,
  SignInButtonImage,
  GuestLoginButtonWrapper,
  BottomBar,
  BottomBarFirstLine,
  BottomBarSecondContainer,
  BottomBarSecondLine,
  BottomBarThirdLine,
} from './styledComponents';
import Styles from './styles';
import RouteParamConstants from 'shared/utils/routeParamConstants';
import SStart from 'shared/screens/Start';
import audio from 'shared/utils/audio';
import Signup from '../Signup';
import ResetPassword from '../ResetPassword';
import ENVIRONMENT_VARIABLES from 'shared/utils/environmentVariables';
import ThemeContext from 'shared/context/ThemeContext';
import TermsModal from '../../components/TermsModal';
import generateRender from 'shared/utils/generateRender';
import SocialLoginInterface from 'shared/api/SocialLoginInterface';
import CopySelector from 'shared/utils/copySelector';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import Button from '../../components/Button/Button';

const DefaultStart = props => {
  const activeBG = require(asset`RoundedRect_Pink@3x.png`);
  const nonActiveBG = require(asset`RoundedRect_Grey@3x.png`);
  const activeCheckbox = require(asset`Checkbox@3x.png`);
  const nonActiveCheckbox = require(asset`Checkbox_Deselected@3x.png`);
  const { Start } = useContext(ThemeContext);

  const isGuestLoginButton = ENVIRONMENT_VARIABLES.PLAY_AS_GUEST_TYPE === 'BUTTON';

  const [state, setState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      oauthLoading: false,
      statusMessage: null,
      emailPermissionAccepted: null,
      socialError: null,
      socialErrorType: null,
      playAsGuest: ENVIRONMENT_VARIABLES.PLAY_AS_GUEST,
      selectedTerms: null,
      error: null,
    }
  );

  useEffect(() => {
    if (props.navigation.getParam(RouteParamConstants.PARAM_ACTION)) {
      _handleActionRedirect();
    }
    if (props.navigation.getParam(RouteParamConstants.PARAM_TRACKING_URL_AFF_ID)) {
      let aff_Id = props.navigation.getParam(RouteParamConstants.PARAM_TRACKING_URL_AFF_ID);
      Array.isArray(aff_Id) ? props.setUserAffId(aff_Id[0]) : props.setUserAffId(aff_Id);
    }
  }, []);

  const _handleActionRedirect = () => {
    window.location.assign(props.navigation.getParam(RouteParamConstants.PARAM_ACTION));
  };

  useEffect(() => {
    props.googleToken &&
      SocialLoginInterface.googleLoginHandler({
        setSuccessState: setSuccessState,
        setErrorState: setErrorState,
        setCanceledState: setCanceledState,
      });
  }, [props.googleToken]);

  const setSuccessState = () => {
    setState({
      oauthLoading: true,
      statusMessage: null,
      socialError: null,
      socialErrorType: null,
      error: null,
    });
  };

  const setErrorState = () => {
    setState({
      oauthLoading: false,
      statusMessage: null,
      socialError: null,
      socialErrorType: null,
      error: true,
    });
  };

  const setCanceledState = ({ statusMessage, emailPermissionAccepted, socialErrorType }) => {
    setState({
      oauthLoading: false,
      statusMessage,
      emailPermissionAccepted,
      socialError: true,
      socialErrorType,
      error: null,
    });
  };

  const closeModal = () => {
    audio.onClick();
    setState({
      statusMessage: null,
      socialError: false,
      socialErrorType: null,
      error: null,
    });
  };

  const closeTermsModal = callback => {
    setState({ selectedTerms: null });
    callback();
  };

  const checkSelectedTerms = item => setState({ selectedTerms: item });

  const clientText = generateRender({
    default: '',
    hri: getText(TEXT_KEY.HRI),
    sga: getText(TEXT_KEY.SGA),
    ti: getText(TEXT_KEY.TI),
  });

  return (
    <SStart
      navigation={props.navigation}
      mountIntent={{
        facebook: () =>
          SocialLoginInterface.facebookLoginHandler({
            FB,
            CopySelector,
            emailPermissionAccepted: state.emailPermissionAccepted,
            setSuccessState: setSuccessState,
            setErrorState: setErrorState,
            setCanceledState: setCanceledState,
          }),
      }}
      render={args => {
        const {
          onGuestActivate,
          onAccountActivate,
          onFacebookActivate,
          isVisible,
          terms,
          isLoading,
          closeModal: argsCloseModal,
          onSubmit,
          setTerms,
          termsAndConditionsUrl,
          privacyPolicyUrl,
          showSignUpForm,
          signupHandler,
          recoverPasswordResponse,
        } = args;

        return (
          <SplashScreen className="SplashScreen">
            <Signup
              isModal
              isVisible={showSignUpForm}
              title={getText(TEXT_KEY.SIGNUP_TITLE)}
              closeModal={argsCloseModal}
              closeRetryModal={closeModal}
              zIndex={101}
              modalStyle={Styles.modalStyle}
              modalContainerStyle={Styles.modalContainerStyle}
              titleStyle={Styles.titleStyle}
              termsAndConditionsUrl={termsAndConditionsUrl}
              privacyPolicyUrl={privacyPolicyUrl}
              navigation={props.navigation}
            />
            <ResetPassword
              isVisible={recoverPasswordResponse.visible}
              title={getText(TEXT_KEY.CHOOSE_PASSWORD)}
              closeModal={argsCloseModal}
              zIndex={101}
              navigation={props.navigation}
              invalid={recoverPasswordResponse.invalid}
              errorMessage={recoverPasswordResponse.message}
              recoverId={recoverPasswordResponse.id}
              recoverToken={recoverPasswordResponse.token}
            />
            <TermsModal
              isVisible={isVisible}
              closeModal={argsCloseModal}
              closeRetryModal={closeModal}
              terms={terms}
              activeCheckbox={activeCheckbox}
              nonActiveCheckbox={nonActiveCheckbox}
              setTerms={setTerms}
              onSubmit={onSubmit}
              activeBG={activeBG}
              nonActiveBG={nonActiveBG}
              onFacebookActivate={onFacebookActivate}
              closeTermsModal={closeTermsModal}
              selectedTerms={state.selectedTerms}
              checkSelectedTerms={checkSelectedTerms}
              error={state.error}
              socialError={state.socialError}
              socialErrorType={state.socialErrorType}
              statusMessage={state.statusMessage}
              clientText={clientText}
            />
            <StartScreenWrapper>
              <LogoImage applyTheme={Start.Logo} src={require(asset`./images/Start/Star_Play_Logo@3x.png`)} />
              <MainContentContainer guestButton={isGuestLoginButton} theme={Start.MainContentContainer}>
                <MainContentHeader>{getText(TEXT_KEY.WELCOME)}!</MainContentHeader>
                <SignInButtonWrapper className="splash-signup-button" onClick={signupHandler}>
                  <SignInButtonImage src={require(asset`./images/Start/Buttons/Guest_Button@3x.png`)} />
                </SignInButtonWrapper>
                <MainContentHeader>{getText(TEXT_KEY.OR)}</MainContentHeader>
                <SignInButtonWrapper className="splash-facebook-button" onClick={onFacebookActivate}>
                  <SignInButtonImage src={require(asset`./images/Start/Buttons/Facebook_Button@3x.png`)} />
                </SignInButtonWrapper>
                {state.playAsGuest === 'true' && isGuestLoginButton && (
                  <GuestLoginButtonWrapper>
                    <Button
                      imageSource={require(asset`./images/Start/Buttons/login-guest-button.png`)}
                      onClick={onGuestActivate}
                      containerStyle={Styles.guestButtonStyle}
                      imageStyle={Styles.guestButtonStyle}
                      objectFit="contain"
                    />
                  </GuestLoginButtonWrapper>
                )}
              </MainContentContainer>
              <BottomBar>
                <BottomBarFirstLine>{getText(TEXT_KEY.ALREADY_HAVE_ACCOUNT)}</BottomBarFirstLine>
                <BottomBarSecondContainer>
                  <div onClick={onAccountActivate}>
                    <BottomBarSecondLine>{getText(TEXT_KEY.LOG_IN_HERE)}</BottomBarSecondLine>
                  </div>
                  {state.playAsGuest === 'true' && !isGuestLoginButton && (
                    <BottomBarThirdLine qa-id="playAsGuestBtn" onClick={onGuestActivate}>
                      {getText(TEXT_KEY.PLAY_AS_GUEST)}
                    </BottomBarThirdLine>
                  )}
                </BottomBarSecondContainer>
              </BottomBar>
            </StartScreenWrapper>
            {isLoading && !state.oauthLoading && (
              <Spinner style={{ position: 'absolute' }} animation="grow" className="custom-spinner" />
            )}
            {state.oauthLoading && !isLoading && (
              <Spinner style={{ position: 'absolute' }} animation="grow" className="custom-spinner" />
            )}
          </SplashScreen>
        );
      }}
    />
  );
};

DefaultStart.path = '';

export default DefaultStart;

